import React from "react";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarGroup, Box, Tooltip, Typography } from "@mui/material";
import { Calendar, Clock } from "@phosphor-icons/react";
import { format, isToday, isTomorrow, isYesterday, isSameWeek } from "date-fns";

import { Appointment } from "api/types/appointments";
import { BILLING_TYPES } from "@constants/index";
import getAvatarInitials from "utils/getAvatarInitials";
import { formatTimeZoneToAbbreviation } from "utils/formatTimeZoneToAbbreviation";
import { BILLING_TYPE_I18N_KEYS } from "../utils";
import { getDateFnsLocale } from "utils/getDateFnsLocale";
import { getIntlLocale } from "utils/getIntlLocale";
import { formatLocalizedDate } from "utils/formatLocalizedDate";

interface AppointmentCardProps {
  appointmentData: Appointment;
}

/**
 * AppointmentCard component for Call Signature Task
 * @param {AppointmentCardProps} appointmentData - The appointment data
 * @returns {React.ReactNode} The AppointmentCard component
 */
const AppointmentCard = ({ appointmentData }: AppointmentCardProps) => {
  const { t } = useTranslation(["dashboard"]);
  const {
    start_date,
    end_date,
    call_timezone,
    parent_name,
    clinician_name,
    client_avatar,
    clinician_avatar,
    billing_type,
  } = appointmentData || {};

  const start = new Date(start_date as string);
  const end = new Date(end_date as string);
  const tzAbbreviation = formatTimeZoneToAbbreviation(call_timezone, start);
  const formattedTime = new Intl.DateTimeFormat(getIntlLocale(), {
    timeZone: call_timezone,
    timeStyle: "short",
  });

  const billingTypeKey = BILLING_TYPE_I18N_KEYS[billing_type as keyof typeof BILLING_TYPES];
  const serviceName = billingTypeKey
    ? t(`dashboard:parent.nextAppointment.billingTypes.${billingTypeKey}`)
    : BILLING_TYPES[billing_type as keyof typeof BILLING_TYPES];

  // if start date is in the current week, show relative date if not, show the date like "Monday, July 7th"
  const startDate = isSameWeek(start, new Date())
    ? isToday(start)
      ? t("dashboard:parent.nextAppointment.today")
      : isTomorrow(start)
        ? t("dashboard:parent.nextAppointment.tomorrow")
        : isYesterday(start)
          ? t("dashboard:parent.nextAppointment.yesterday")
          : format(start, "EEEE", { locale: getDateFnsLocale() })
    : formatLocalizedDate(start, { en: "MMMM do", es: "d 'de' MMMM" });

  const eventAttendees = [
    {
      id: "attendee-1",
      name: parent_name,
      avatar: client_avatar ?? null,
    },
    {
      id: "attendee-2",
      name: clinician_name,
      avatar: clinician_avatar ?? null,
    },
  ];

  return (
    <Box sx={{ backgroundColor: "grey.50", borderRadius: 2, p: 6, my: 7 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" px={0} py={0}>
        <AvatarGroup max={3} sx={{ justifyContent: "flex-end" }}>
          {eventAttendees.map((attendee) => (
            <Tooltip key={attendee.id} title={attendee.name} arrow placement="top">
              <Avatar
                alt={attendee.name}
                src={attendee.avatar ?? undefined}
                sx={{ width: 40, height: 40, fontSize: "12px" }}
                data-testid={`avatar-item-${attendee.id}`}
              >
                {getAvatarInitials(attendee.name)}
              </Avatar>
            </Tooltip>
          ))}
        </AvatarGroup>
      </Box>

      <Typography component="h3" variant="h2" sx={{ mt: 2, fontWeight: 500 }}>
        {serviceName}
      </Typography>

      <Box display="flex" flexWrap="wrap" columnGap={6}>
        <Typography component="p" variant="body1" color="text.secondary">
          <span style={{ verticalAlign: "middle", lineHeight: "1", display: "inline-block" }}>
            <Clock size={20} weight="duotone" />
          </span>{" "}
          {formattedTime.format(start)} - {formattedTime.format(end)} ({tzAbbreviation})
        </Typography>

        <Typography component="p" variant="body1" color="text.secondary" textTransform="capitalize">
          <span style={{ verticalAlign: "middle", lineHeight: "1", display: "inline-block" }}>
            <Calendar size={20} weight="duotone" />
          </span>{" "}
          {startDate}
        </Typography>
      </Box>
    </Box>
  );
};

export default AppointmentCard;
